import {
  Auth0ContextInterface,
  Auth0Provider,
  AuthorizationParams,
  useAuth0,
} from "@auth0/auth0-react";
import { useMemo } from "react";
import {
  AppState,
  IdPClientConfig,
  IdPClientInterface,
  IdPClientRootOptions,
  IdPClientState,
  IdPIssuerMetadata,
  IdPLogoutOptions,
  IdPRedirectLoginOptions,
  IdPRedirectResult,
  IdPTokens,
} from "./types";

/**
 * The Auth0 SDK calls this adaptor needs. Narrowed so tests can pass plain functions.
 */
export type Auth0Methods = Pick<
  Auth0ContextInterface,
  "loginWithRedirect" | "handleRedirectCallback" | "getAccessTokenSilently" | "logout"
>;

/**
 * This is an adaptor that implements IdPClientInterface against Auth0 (`@auth0/auth0-react`).
 * It is the only file in the app that imports the Auth0 SDK.
 */
export class Auth0IdPClient implements IdPClientInterface {
  // Properties
  private readonly auth0: Auth0Methods;
  private readonly config: IdPClientConfig;

  // Constructor provide the Auth0 SDK methods and the tenant config.
  constructor(auth0: Auth0Methods, config: IdPClientConfig) {
    this.auth0 = auth0;
    this.config = config;
  }

  getIssuerMetadata(): IdPIssuerMetadata[] {
    const { domain, proxyDomainForAuthorize } = this.config;
    return [
      {
        issuer: `https://${domain}/`,
        authorization_endpoint: `https://${proxyDomainForAuthorize || domain}/authorize`,
        // Auth0's discovery document advertises no end_session_endpoint; /v2/logout is Auth0's own.
        end_session_endpoint: `https://${domain}/v2/logout`,
      },
    ];
  }

  // In order to support a proxy domain for the authorize endpoint we need to provide a different
  // openUrl function to the auth0 loginWithRedirect function.
  openUrl = async (urlWithFragment: string): Promise<void> => {
    const { domain, proxyDomainForAuthorize } = this.config;
    // If we have a proxy domain for authorize we need to replace the existing domain with the proxy domain
    if (proxyDomainForAuthorize) {
      urlWithFragment = urlWithFragment.replace(domain, proxyDomainForAuthorize);
    }
    window.location.assign(urlWithFragment);
  };

  async loginWithRedirect(opts: IdPRedirectLoginOptions): Promise<void> {
    const { appState, authorizationParams } = opts;
    await this.auth0.loginWithRedirect({
      appState,
      // Auth0 narrows `prompt` to its own literals; the values are passed through unchanged.
      authorizationParams: authorizationParams as AuthorizationParams,
      openUrl: this.openUrl,
    });
  }

  async handleRedirectCallback(url?: string): Promise<IdPRedirectResult> {
    const { appState } = await this.auth0.handleRedirectCallback(url);
    return { appState: appState as AppState | undefined };
  }

  async getTokensSilently(): Promise<IdPTokens> {
    const { access_token, id_token, expires_in } = await this.auth0.getAccessTokenSilently({
      authorizationParams: {
        audience: this.config.audience,
      },
      detailedResponse: true,
    });
    return { access_token, id_token, expires_in };
  }

  async logout(opts: IdPLogoutOptions): Promise<void> {
    const { returnTo, idTokenHint, openUrl } = opts;
    await this.auth0.logout({
      ...(openUrl !== undefined && { openUrl }),
      logoutParams: {
        returnTo,
        // Auth0's /v2/logout ignores id_token_hint; it is forwarded so the interface holds for IdPs
        // that require it.
        ...(idTokenHint && { id_token_hint: idTokenHint }),
      },
    });
  }
}

/**
 * Mounts the Auth0 SDK. Auth0 exchanges the code itself when it mounts on the callback route and
 * reports the result through `onRedirectCallback`.
 */
export const IdPClientRoot = ({
  children,
  domain,
  clientId,
  audience,
  redirectUri,
  onRedirectCallback,
}: IdPClientRootOptions): JSX.Element => (
  <Auth0Provider
    domain={domain}
    clientId={clientId}
    authorizationParams={{
      audience: audience,
      redirect_uri: redirectUri,
    }}
    onRedirectCallback={(appState, user) =>
      onRedirectCallback(appState as AppState | undefined, user)
    }>
    {children}
  </Auth0Provider>
);

/**
 * Returns the IdP client and the IdP session state. Must be rendered inside IdPClientRoot.
 */
export function useIdPClient(config: IdPClientConfig): IdPClientState {
  const {
    loginWithRedirect,
    handleRedirectCallback,
    getAccessTokenSilently,
    logout,
    isAuthenticated,
    isLoading,
    user,
  } = useAuth0();
  const { domain, clientId, audience, proxyDomainForAuthorize, redirectUri } = config;

  const client = useMemo(
    () =>
      new Auth0IdPClient(
        { loginWithRedirect, handleRedirectCallback, getAccessTokenSilently, logout },
        { domain, clientId, audience, proxyDomainForAuthorize, redirectUri }
      ),
    [
      loginWithRedirect,
      handleRedirectCallback,
      getAccessTokenSilently,
      logout,
      domain,
      clientId,
      audience,
      proxyDomainForAuthorize,
      redirectUri,
    ]
  );

  return useMemo(
    () => ({ client, isAuthenticated, isLoading, user }),
    [client, isAuthenticated, isLoading, user]
  );
}
